import { OptionsApi } from '@core/swagger-client';
import { Button, Delete, ErrorMessage, Heading, Upload, useFileDropzone } from '@core/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Must match EmailStyleController: the header cell is 580 px wide, every existing header is 160 px high.
export const HEADER_IMAGE_WIDTH = 580;
export const HEADER_IMAGE_HEIGHT = 160;
const MAX_HEADER_IMAGE_BYTES = 10 * 1024 * 1024;

/** Whether the image is smaller than the header in either direction; false when it cannot be measured (the server decides then). */
export const isSmallerThanHeader = async (file: Blob): Promise<boolean> => {
    if (typeof createImageBitmap !== 'function') return false;
    try {
        const bitmap = await createImageBitmap(file);
        const isSmaller = bitmap.width < HEADER_IMAGE_WIDTH || bitmap.height < HEADER_IMAGE_HEIGHT;
        bitmap.close();
        return isSmaller;
    } catch {
        return false;
    }
};

type EmailHeaderImageProps = {
    /** The uploaded or adopted image; null while the default is used. */
    headerImageUrl: string | null;
    defaultHeaderImageUrl: string;
    /** Called with the new URL after an upload, with null after "use default image". */
    onChange: (headerImageUrl: string | null) => void;
};

/**
 * Upload / replace / use-default for the e-mail header. Takes effect at once (like the banner image) and reports
 * the new URL to the page, which updates its preview without refetching the style: a refetch would reset the
 * rest of the unsaved form.
 */
export const EmailHeaderImage = ({ headerImageUrl, defaultHeaderImageUrl, onChange }: EmailHeaderImageProps) => {
    const { t } = useTranslation();
    const [isTooSmall, setIsTooSmall] = useState(false);
    const queryClient = useQueryClient();
    // Set from the moment a file is accepted (before the size check) until its upload settles: the mutation's own
    // isPending only starts after the check, and two uploads in flight could finish in either order.
    const isUploadingRef = useRef(false);
    const uploadMutation = useMutation(OptionsApi.emailStyle.uploadHeaderImage());
    const deleteMutation = useMutation(OptionsApi.emailStyle.deleteHeaderImage());

    // Let a later visit to the page load the new URL, without refetching now: that would reset the unsaved form.
    const markStyleStale = () => queryClient.invalidateQueries({ queryKey: ['emailStyle', 'get'], refetchType: 'none' });

    const { getRootProps, getInputProps, open, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        // A click on the image opens the picker, like the banner and profile image. No keyboard stop of its own: it
        // would have no accessible name, and the Upload button below offers the same action.
        noKeyboard: true,
        disabled: uploadMutation.isPending,
        maxSize: MAX_HEADER_IMAGE_BYTES,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/webp': ['.webp']
        },
        onDrop: async (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (!file || isUploadingRef.current) return;

            isUploadingRef.current = true;
            try {
                setIsTooSmall(await isSmallerThanHeader(file));
            } catch (error) {
                isUploadingRef.current = false;
                throw error;
            }
            uploadMutation.mutate(
                { file },
                {
                    onSuccess: (url) => {
                        deleteMutation.reset();
                        markStyleStale();
                        onChange(url);
                    },
                    // The warning describes the file that was just picked, which never made it to the server.
                    onError: () => setIsTooSmall(false),
                    onSettled: () => {
                        isUploadingRef.current = false;
                    }
                }
            );
        }
    });

    const revertToDefault = () =>
        deleteMutation.mutate(undefined, {
            onSuccess: () => {
                setIsTooSmall(false);
                uploadMutation.reset();
                markStyleStale();
                onChange(null);
            }
        });

    return (
        <section className="mb-10">
            <Heading variant="h2" classNames="mb-2">
                {t('Email.Style.HeaderImage.Heading')}
            </Heading>
            <p className="mb-4">{t('Email.Style.HeaderImage.Info')}</p>
            {/* Same drop-area look as the banner and profile image: the dashed border is a layer over the image (so the
                image keeps its full 580 px) and lights up while dragging or hovering. */}
            <div
                {...getRootProps()}
                className={`group relative w-full max-w-[580px] rounded-lg overflow-hidden ${isDragActive ? 'bg-accent-lighter' : ''} hover:bg-accent-lighter transition-colors duration-150`}
            >
                <img
                    src={headerImageUrl ?? defaultHeaderImageUrl}
                    alt={t('Email.Style.HeaderImage.CurrentImageAlt')}
                    width={HEADER_IMAGE_WIDTH}
                    height={HEADER_IMAGE_HEIGHT}
                    className="block w-full h-auto"
                />
                <div
                    className={`absolute inset-0 rounded-lg border-2 border-dashed ${isDragActive ? 'border-accent' : 'border-transparent'} group-hover:border-accent transition-colors duration-150`}
                ></div>
                <input {...getInputProps()} />
            </div>
            <p className="mt-2">
                {t('Generic.DragDrop.AcceptedText')}
                <span className="font-medium">{t('Email.Style.HeaderImage.AcceptedTypes')}</span>
            </p>
            <p className="text-sm">{t('Email.Style.HeaderImage.FileLimits')}</p>
            <div className="flex flex-wrap gap-4 mt-4">
                <Button type="button" variant="primary-outline" onClick={() => open()} isLoading={uploadMutation.isPending}>
                    <Upload classNames="size-6 flex-none stroke-current me-2" />
                    {t('Generic.Button.UploadImage')}
                </Button>
                {headerImageUrl && (
                    <Button type="button" variant="warning-outline" onClick={revertToDefault} isLoading={deleteMutation.isPending}>
                        <Delete classNames="size-6 flex-none stroke-current me-2" />
                        {t('Email.Style.HeaderImage.Button.UseDefault')}
                    </Button>
                )}
            </div>
            {/* Always mounted: screen readers announce text added to an existing live region, not a new one. */}
            <p role="status" className={isTooSmall ? 'mt-4' : undefined}>
                {isTooSmall && t('Email.Style.HeaderImage.Warning.TooSmall')}
            </p>
            {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
            {uploadMutation.error && <ErrorMessage apiError={uploadMutation.error} classNames="mt-4" />}
            {deleteMutation.error && <ErrorMessage apiError={deleteMutation.error} classNames="mt-4" />}
        </section>
    );
};
